import React, { useState, useEffect } from "react";
import { API_BASE } from "../config";

const T = {
  primary: "#2A9D8F",
  primaryDark: "#1F7A6E",
  accentGold: "#D4A017",
  accentGoldDark: "#B8880F",
  navy: "#1A3A4A",
  navyDeep: "#0D2B3E",
};

const ScrollingAnnouncementBanner = ({ eventId }) => {
  const [apiAnnouncements, setApiAnnouncements] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const tickerAnnouncements = [
    "🎉 Early Bird Registration Ends Soon! Save up to 40%",
    "📢 Submit Your Abstract by March 31st, 2026",
    "🏆 Best Paper Award: USD 1000 Prize Money",
    "🤝 Join us for Networking Events & Workshops",
    "📍 Venue: Hotel American Palace Eur, Rome, Italy",
  ];

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        setIsLoading(true);
        // Updated API endpoint with event support
        const url = eventId
          ? `${API_BASE}/api/submit-announcements/event/${eventId}`
          : `${API_BASE}/api/submit-announcements/active`;
        
        console.log("Fetching announcements from:", url);
        const res = await fetch(url, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        console.log("Announcements response:", data);
        
        // Fix: Handle different response structures
        // The backend returns { success, submitAnnouncements } array
        let activeAnnouncement = null;
        
        if (data.success) {
          // Check if we have submitAnnouncements array (from event endpoint)
          if (data.submitAnnouncements && Array.isArray(data.submitAnnouncements)) {
            // Find the active announcement (isActive === true)
            activeAnnouncement = data.submitAnnouncements.find(a => a.isActive === true);
          } 
          // Check if we have submitAnnouncement object (from /active endpoint)
          else if (data.submitAnnouncement && data.submitAnnouncement.isActive && data.submitAnnouncement.message) {
            activeAnnouncement = data.submitAnnouncement;
          }
        }
        
        if (activeAnnouncement && activeAnnouncement.message) {
          console.log("Found active announcement:", activeAnnouncement.message);
          setApiAnnouncements([activeAnnouncement.message]);
        } else {
          console.log("No active announcement found, using fallback");
          setApiAnnouncements(null);
        }
      } catch (error) {
        console.error("Announcement error:", error);
        setApiAnnouncements(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnnouncements();
    const interval = setInterval(fetchAnnouncements, 30000);
    return () => clearInterval(interval);
  }, [eventId]);

  const getCurrentDate = () => {
    return new Date().toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const items = apiAnnouncements || tickerAnnouncements;
  const repeated = Array(8).fill(items).flat();

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes ticker-scroll {
        0% { transform: translateX(0); }
        100% { transform: translateX(-50%); }
      }
      @keyframes pulse-dot {
        0%,100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.5; transform: scale(0.8); }
      }
      .ticker-track {
        display: inline-flex;
        align-items: center;
        white-space: nowrap;
        width: max-content;
        animation: ticker-scroll 80s linear infinite;
      }
      .ticker-track:hover {
        animation-play-state: paused;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  if (isLoading && !apiAnnouncements) {
    return (
      <div
        style={{
          background: `linear-gradient(135deg, ${T.accentGold} 0%, ${T.accentGoldDark} 100%)`,
          color: T.navyDeep,
          height: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          zIndex: 10000,
        }}
      >
        <span>Loading announcements...</span>
      </div>
    );
  }

  return (
    <div
      style={{
        background: `linear-gradient(135deg, ${T.accentGold} 0%, ${T.accentGoldDark} 100%)`,
        color: T.navyDeep,
        height: 56,
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
        position: "relative",
        zIndex: 10000,
      }}
    >
      <div
        style={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "0 24px",
          height: "100%",
          background: "rgba(0,0,0,0.1)",
          borderRight: "1px solid rgba(0,0,0,0.1)",
          whiteSpace: "nowrap",
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: "0.16em",
          zIndex: 2,
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: T.navyDeep,
            display: "inline-block",
            animation: "pulse-dot 1.4s ease infinite",
          }}
        />
        {apiAnnouncements ? "SUMMIT ANNOUNCEMENT" : "LIVE UPDATES"}
      </div>

      <div style={{ overflow: "hidden", flex: 1 }}>
        <div className="ticker-track">
          {repeated.map((item, i) => (
            <span
              key={i}
              style={{
                display: "inline-flex",
                alignItems: "center",
                whiteSpace: "nowrap",
                fontSize: 14,
                fontWeight: 600,
                color: T.navyDeep,
                letterSpacing: "0.01em",
                padding: "0 16px",
              }}
            >
              <span>📅 {getCurrentDate()}</span>
              <span style={{ margin: "0 12px", opacity: 0.4 }}>|</span>
              <span>{item}</span>
              <span style={{ margin: "0 12px", opacity: 0.4 }}>|</span>
            </span>
          ))}
        </div>
      </div>

      <div
        style={{
          position: "absolute",
          right: 0,
          top: 0,
          width: 80,
          height: "100%",
          background: `linear-gradient(to right, transparent, ${T.accentGoldDark})`,
          pointerEvents: "none",
        }}
      />
    </div>
  );
};

export default ScrollingAnnouncementBanner;